import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open a Tender currently in quotation.
 */
const OPEN_TENDER_ROUTE = "tenders/TenderOpenQuotation";

/**
 * Tender workflow state used by the backend for In Quotation.
 */
const TENDER_STATE_IN_QUOTATION = 1;

/**
 * Fallback permissions used when the parent does not provide role permissions.
 */
const READ_ONLY_PERMISSIONS = ["Read", "View"];

//endregion

//region Helpers

/**
 * Builds a safe display name for Tender tab labels.
 *
 * Purpose:
 * - supports RequestForQuotes as an object or as a direct string;
 * - avoids rendering undefined/null in tab labels.
 *
 * @param {object|null|undefined} tender - Tender row.
 * @returns {string} Safe tender display name.
 */
const getTenderDisplayName = (tender) =>
  tender?.RequestForQuotes?.Name || tender?.RequestForQuotes || "Tender";

/**
 * Normalizes role permissions received from the parent.
 *
 * Purpose:
 * - supports role objects with Value;
 * - supports plain string permissions;
 * - keeps a safe read-only fallback.
 *
 * @param {Array<object|string>|undefined} roles - Incoming roles.
 * @returns {Array<string>} Role permission values.
 */
const getRolePermissions = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) {
    return READ_ONLY_PERMISSIONS;
  }

  const values = roles
    .map((role) => role?.Value ?? role)
    .filter(Boolean);

  return values.length ? values : READ_ONLY_PERMISSIONS;
};

//endregion

//region Component

/**
 * Renders the Tenders In Quotation configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in compact tabs mode;
 * - show Spot or Non-Spot title/description according to mode;
 * - list only tenders matching the provided TenderTypeId and In Quotation state;
 * - expose only the View workflow;
 * - open the TenderOpenQuotation page in a tab with the selected Tender.
 *
 * Important:
 * - this component is rendered inside a tabs page;
 * - therefore it uses the config-page--tabs modifier;
 * - MTable uses "Update" internally for Edit, so "Update" must be removed.
 *
 * @param {object} props - Component props.
 * @param {number} props.tenderTypeId - Tender type id used in the table filter.
 * @param {"spot"|"nonspot"} props.mode - Tender mode used for title/description labels.
 * @param {Array<object|string>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Tenders In Quotation tab.
 */
export default function TenderInQuotation({
  tenderTypeId,
  mode,
  roles,
  onCreateTab,
  toastMessages,
}) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [refresh, setRefresh] = useState(false);

  const isSpot = mode === "spot";

  /**
   * Keeps table permissions stable and resilient to different role payload shapes.
   */
  const rolePermissions = useMemo(
    () => getRolePermissions(roles),
    [roles]
  );

  /**
   * Removes table actions that are not required in this read-only workflow tab.
   */
  const removeContextItems = useMemo(
    () => ["Print", "Copy", "Create", "Update", "Delete", "Audit"],
    []
  );

  /**
   * Opens the selected Tender in the In Quotation workflow page.
   *
   * Flow:
   * - validates row selection;
   * - persists operation and selected Tender in M_State;
   * - opens the workflow route in a new tab.
   *
   * @param {"View"} operation - Requested operation.
   * @param {object|null|undefined} row - Selected Tender row.
   * @returns {void}
   */
  const openTenderInQuotation = useCallback(
    (operation, row) => {
      if (!row) {
        toastMessages?.("error", "", t("SelectedLine"));
        return;
      }

      dispatch(
        M_StateActions.addM_State({
          key: "operation",
          value: operation,
        })
      );

      dispatch(
        M_StateActions.addM_State({
          key: "TenderLine",
          value: row,
        })
      );

      const tabLabel = `${t("LabelInQuotationTender")} "${getTenderDisplayName(row)}"`;

      onCreateTab?.(OPEN_TENDER_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  /**
   * Replaces MTable default CRUD handlers with the In Quotation workflow.
   */
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ["View", (row) => openTenderInQuotation("View", row)],
      ]),
    [openTenderInQuotation]
  );

  return (
    <div
      className="config-page config-page--tabs"
      style={{ "--config-page-title-color": ctx.secondaryColor }}
    >
      <div className="config-page__header">
        <div className="config-page__title">
          {isSpot
            ? t("SpotTendersInQuotation")
            : t("NonSpotTendersInQuotation")}
        </div>

        <div className="config-page__description">
          {isSpot
            ? t("InfoSpotTendersInQuotation")
            : t("InfoNonSpotTendersInQuotation")}
        </div>
      </div>

      <div className="config-page__table">
        <MTable
          filtersOnTable
          entityName="Tenders"
          roleName="RequestForQuotes"
          rolePermissions={rolePermissions}
          removeContextItems={removeContextItems}
          refresh={refresh}
          setRefresh={setRefresh}
          useSessionStorage={false}
          FILTER={`TenderTypeId eq ${tenderTypeId} and State eq ${TENDER_STATE_IN_QUOTATION}`}
          replaceFunctions={replaceFunctions}
          tabsRailWidth={250}
        />
      </div>
    </div>
  );
}

//endregion

//region PropTypes

TenderInQuotation.propTypes = {
  tenderTypeId: PropTypes.number.isRequired,
  mode: PropTypes.oneOf(["spot", "nonspot"]),
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func,
};

//endregion